import type { Response } from "@/domain/gems-game/data-access/lib/cms";
import type { InjectableFetch } from "@/domain/gems-game/data-access/lib/types";
import { match } from "ts-pattern";

export type CMSTypeOption = {
  name: string;
  id: string;
};

export type CMSField = {
  slug: string;
  validations: {
    options: CMSTypeOption[];
  };
};

export type FeedTypes = {
  questTypes: Record<string, string>; // id -> name
  onboardingExperiences: Record<string, string>; // id -> name
  lastUpdated: string;
};

export type CMSResponse = {
  fields: CMSField[];
  lastUpdated: string;
};

export const getFeedTypes = async (
  fetch: InjectableFetch,
  collectionItemsUrl: URL,
): Promise<FeedTypes> => {
  const res = await fetch<Response<CMSResponse>, Error>(
    collectionItemsUrl.toString(),
    {},
  );

  return await match(res)
    .with({ ok: true }, async (res) => {
      const responseData = await res.json();
      const data = responseData as unknown as CMSResponse;

      // Find the quest-type and onboarding-experience fields
      const questTypeField = data.fields.find(
        (field: CMSField) => field.slug === "quest-type",
      );
      const onboardingExperienceField = data.fields.find(
        (field: CMSField) => field.slug === "onboarding-experience",
      );

      if (!questTypeField || !onboardingExperienceField) {
        throw new Error("Required CMS fields not found");
      }

      // Convert options arrays to records
      const questTypes = questTypeField.validations.options.reduce(
        (acc: Record<string, string>, option: CMSTypeOption) => {
          acc[option.id] = option.name;
          return acc;
        },
        {},
      );

      const onboardingExperiences =
        onboardingExperienceField.validations.options.reduce(
          (acc: Record<string, string>, option: CMSTypeOption) => {
            acc[option.id] = option.name;
            return acc;
          },
          {},
        );

      return {
        questTypes,
        onboardingExperiences,
        lastUpdated: data.lastUpdated,
      };
    })
    .with({ ok: false }, async (res) => {
      const text = await res.text();
      throw new Error(`Error from API: ${text}`);
    })
    .exhaustive();
};
